import {useEffect, useState, useCallback} from 'react';
import {useQueries} from '@tanstack/react-query';
import {Contract, Shipment, ShipmentPreferredCurrencyEnum} from '@shipwell/backend-core-singlerequestparam-sdk';
import invariant from 'tiny-invariant';
import {useSelectedContract, isApplicableContract} from '../contracts/useSelectedContract';
import {ApplicableContractWithCharges} from '../contracts/useGetApplicableContracts';
import {CARRIER_CAPACITY_AVAILABILITY_QUERY_KEY, CARRIER_RELATIONSHIPS_QUERY_KEY} from 'App/data-hooks/queryKeys';
import {getCarrierCapacityAvailability} from 'App/api/carriers/carrierCapacity';
import {formatCarrierPOCs} from 'App/utils/carrierHelpers';
import {getCarrierRelationshipsByIdPromise} from 'App/api/carriers/carrierRelationship';

interface BulkTenderContract {
  shipments: Shipment[];
  shipment?: never;
}

interface SingleTenderContract {
  shipment: Shipment;
  shipments?: never;
}

export const useTenderContract = ({shipments, shipment}: BulkTenderContract | SingleTenderContract) => {
  const [selectedMultiApplicableContracts, setSelectedMultiApplicableContracts] = useState<
    ApplicableContractWithCharges[]
  >([]);
  const [capacityAvailable, setCapacityAvailable] = useState(true);

  const {selectedApplicableContract, handleContractChange, isLoading, contractCarrierRelationship, chargeLineItems} =
    useSelectedContract();

  const selectedContract = selectedApplicableContract?.contract;

  // used for the following carrier capacity queries
  const shipmentIds = shipment ? [shipment.id] : shipments?.map((shipment) => shipment.id);
  // we now allow users to select multiple contracts
  const contractIds = selectedContract
    ? [selectedContract.id]
    : selectedMultiApplicableContracts?.map((applicableContract) => applicableContract.contract?.id);

  const carrierCapacityAvailableBulkQueries = useQueries({
    queries: shipmentIds?.flatMap((id) =>
      contractIds.map((contractId) => ({
        queryKey: [CARRIER_CAPACITY_AVAILABILITY_QUERY_KEY, id, contractId],
        queryFn: () => getCarrierCapacityAvailability({shipmentId: id, contractId: contractId || ''}),
        enabled: !!contractIds.length && !!shipmentIds.length
      }))
    )
  });

  useEffect(() => {
    if (carrierCapacityAvailableBulkQueries.every((query) => query.isSuccess)) {
      setCapacityAvailable(carrierCapacityAvailableBulkQueries.every((query) => query.data?.data.capacity_available));
    }
  }, [carrierCapacityAvailableBulkQueries]);

  // used when muliple contracts are selected
  const carrierRelationshipsQueries = useQueries({
    queries: selectedMultiApplicableContracts.map((applicableContract) => ({
      queryKey: [CARRIER_RELATIONSHIPS_QUERY_KEY, applicableContract.contract?.carrier_relationship],
      queryFn: () => getCarrierRelationshipsByIdPromise(applicableContract.contract?.carrier_relationship || ''),
      enabled: !!selectedMultiApplicableContracts.length
    }))
  });

  const getShipmentEquipmentAndMode = () => {
    let equipmentType;
    let mode;
    if (!shipment) {
      const equipmentTypes = shipments.map((shipment) => shipment.equipment_type?.machine_readable);
      assertAllEqual(equipmentTypes, 'equipment_type');
      equipmentType = equipmentTypes[0];

      const modes = shipments.map((shipment) => shipment.mode?.id);
      assertAllEqual(modes, 'mode');
      mode = modes[0];

      const preferredCurrencies = shipments.map((shipment) => shipment.preferred_currency);
      assertAllEqual(preferredCurrencies, 'preferred_currency');
    } else {
      equipmentType = shipment.equipment_type?.machine_readable;
      mode = shipment.mode?.id;
    }

    return {equipmentType, mode};
  };

  //returns a single payload object if tendering using a single contract
  const getContractValues = (formValues?: {
    expires_at?: string;
    expires_after_seconds?: string;
    info_message: string;
    contractRateCurrency?: ShipmentPreferredCurrencyEnum;
  }) => {
    const {equipmentType, mode} = getShipmentEquipmentAndMode();

    const payload = {
      contract: selectedContract?.id,
      equipment_type: equipmentType,
      ...(formValues?.expires_at ? {expires_at: formValues.expires_at} : {}),
      ...(formValues?.expires_after_seconds ? {expires_after_seconds: formValues.expires_after_seconds} : {}),
      info_message: formValues?.info_message || '',
      message: '',
      mode,
      shipment: shipmentIds,
      tender_to_company: formatCarrierPOCs([contractCarrierRelationship])
    };
    return payload;
  };

  //returns an array of payloads to POST if tendering using multiple contracts
  const getMultiContractsValues = (formValues?: {
    expires_at?: string;
    expires_after_seconds?: string;
    info_message: string;
    contractRateCurrency?: ShipmentPreferredCurrencyEnum;
  }) => {
    const {equipmentType, mode} = getShipmentEquipmentAndMode();

    const payloads = selectedMultiApplicableContracts.map((applicableContract, index) => ({
      contract: applicableContract.contract?.id,
      equipment_type: equipmentType,
      ...(formValues?.expires_at ? {expires_at: formValues.expires_at} : {}),
      ...(formValues?.expires_after_seconds ? {expires_after_seconds: formValues.expires_after_seconds} : {}),
      info_message: formValues?.info_message || '',
      message: '',
      mode,
      shipment: shipmentIds,
      tender_to_company: formatCarrierPOCs([carrierRelationshipsQueries[index].data?.data])
    }));
    return payloads;
  };

  return {
    selectedApplicableContract,
    selectedMultiApplicableContracts,
    handleContractChange: useCallback(
      (value: (Contract | ApplicableContractWithCharges) | (Contract | ApplicableContractWithCharges)[]) =>
        Array.isArray(value)
          ? setSelectedMultiApplicableContracts(
              value.map((value) => (isApplicableContract(value) ? value : {contract: value}))
            )
          : handleContractChange(value),
      [handleContractChange]
    ),
    isLoading:
      isLoading ||
      carrierRelationshipsQueries.some((query) => query.isFetching) ||
      carrierCapacityAvailableBulkQueries.some((query) => query.isFetching),
    carrierCapacityIsAvailable: capacityAvailable,
    getContractValues,
    getMultiContractsValues,
    contractCarrierRelationship,
    chargeLineItems,
    hasSelectedContracts: !!selectedApplicableContract || !!selectedMultiApplicableContracts?.length
  };
};

const assertAllEqual = (arr: (string | number | null | undefined)[], name: string) => {
  const isAllEqual = arr.every((val) => val === arr[0]);
  const values = JSON.stringify(arr);
  invariant(
    isAllEqual,
    `Expected all shipments to have the same '${name}', but received the following values for '${name}': ${values}`
  );
};
